import {Box} from '@mantine/core';
import type {FC, ReactNode} from 'react';
import FormSVG from 'src/Assets/ui/svg/formSVG';

/**
 * Empty-state panel: centered message (with optional icon, heading, and a
 * "+ Add New" or custom call to action) over a watermark form illustration.
 * @registry Empty-state panel with a watermark illustration; optional icon, heading, and CTA slots.
 *
 * Ported from `library/elements/NoItemsPanel`. DEVIATION: the original used
 * MUI `Box`/`sx` and a co-located `library/elements/styles.ts` object
 * (`noListItemsWrapper`, `textPlaceholder`, `addNewButtonMock`,
 * `iconBackground`), both banned imports in `design/`. Rewritten with
 * Mantine `Box` style props, inlining those four style rules directly (no
 * other consumers of `styles.ts` are affected). Importing `src/Assets` is
 * allowed here per the same precedent as `design/molecules/TableBodyRender`.
 */
export interface NoItemsPanelProps {
  canCreate?: boolean;
  createNode?: ReactNode;
  noItemsText?: string;
  icon?: ReactNode;
  heading?: ReactNode;
}

export const NoItemsPanel: FC<NoItemsPanelProps> = ({
  canCreate,
  createNode,
  noItemsText,
  icon,
  heading,
}) => {
  // Message text: default "+ Add New" prompt only when canCreate has no custom
  // CTA; otherwise noItemsText renders alongside any createNode.
  const message =
    canCreate && !createNode ? (
      <>
        No items available, click the
        <Box
          component="span"
          c="blue"
          style={{
            backgroundColor: 'Gainsboro',
            padding: '0.5rem 1.5rem',
            margin: '0rem 0.25rem',
            borderRadius: '0.25rem',
          }}>
          + Add New
        </Box>{' '}
        button to create a new item
      </>
    ) : (
      noItemsText ?? (canCreate ? undefined : 'No data found.')
    );
  const cta = canCreate && createNode ? createNode : undefined;

  return (
    <Box mt="1rem" maw="100%" ta="center" c="darkgray">
      <Box fz={{base: '1rem', sm: '1.25rem'}} mb="md">
        {icon && <Box mb="sm">{icon}</Box>}
        {heading && (
          <Box fz="1.5rem" fw={600} mb="xs">
            {heading}
          </Box>
        )}
        {message}
        {cta && <Box mt="md">{cta}</Box>}
      </Box>
      <Box
        maw="20rem"
        mx="auto"
        style={{display: 'block', fill: '#f8f8f8', opacity: 0.8}}>
        <FormSVG />
      </Box>
    </Box>
  );
};
